'use client';

import { Listbox, Transition } from '@headlessui/react';
import {
    ArrowUpTrayIcon,
    CheckIcon,
    ChevronDownIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import ImageUpload from '../ImageUpload';
import { MediaPanel } from '../MediaPanel';
import { useEditor } from '../context/EditorContext/EditorContext';
import ICON_DATA from './fontawesome-data.json';

const STYLE_TOKEN_MAP = {
    'fa-solid': 'solid',
    'fa-regular': 'regular',
    'fa-light': 'light',
    'fa-thin': 'thin',
    'fa-brands': 'brands',
};

const ANIMATION_AND_UTILITY = new Set([
    'fa-spin',
    'fa-spin-pulse',
    'fa-spin-reverse',
    'fa-beat',
    'fa-beat-fade',
    'fa-bounce',
    'fa-fade',
    'fa-flip',
    'fa-shake',
    'fa-rotate-90',
    'fa-rotate-180',
    'fa-rotate-270',
    'fa-rotate-by',
    'fa-flip-horizontal',
    'fa-flip-vertical',
    'fa-fw',
    'fa-2xs',
    'fa-xs',
    'fa-sm',
    'fa-lg',
    'fa-xl',
    'fa-2xl',
    'fa-3x',
    'fa-4x',
    'fa-5x',
    'fa-6x',
    'fa-7x',
    'fa-8x',
    'fa-9x',
    'fa-10x',
]);

const RESTART_PREFIX = 'fa-anim-restart-';

function stripRestartTokens(str) {
    return String(str || '')
        .split(/\s+/)
        .filter((t) => t && !t.startsWith(RESTART_PREFIX))
        .join(' ')
        .trim();
}

const ICON_PACKS = [
    { key: 'classic', label: 'Classic' },
    { key: 'duotone', label: 'Duotone' },
    { key: 'sharp', label: 'Sharp' },
    { key: 'sharp-duotone', label: 'Sharp Duotone' },
    { key: 'brands', label: 'Brands' },
    { key: 'custom', label: 'Custom' },
];

const PACK_TOKEN = {
    classic: [],
    duotone: ['fa-duotone'],
    sharp: ['fa-sharp'],
    'sharp-duotone': ['fa-sharp-duotone'],
    brands: ['fa-brands'],
    custom: [],
};

const STYLE_LABELS = {
    solid: 'Solid',
    regular: 'Regular',
    light: 'Light',
    thin: 'Thin',
    duotone: 'Duotone',
    brands: 'Brands',
};

const STYLE_ALIASES = {};

const ANIMATION_OPTIONS = [
    { id: '', label: 'No Animation', tokens: [] },
    { id: 'spin', label: 'Spin', tokens: ['fa-spin'] },
    { id: 'spin-pulse', label: 'Spin Pulse', tokens: ['fa-spin-pulse'] },
    { id: 'beat', label: 'Beat', tokens: ['fa-beat'] },
    { id: 'bounce', label: 'Bounce', tokens: ['fa-bounce'] },
    { id: 'fade', label: 'Fade', tokens: ['fa-fade'] },
    { id: 'flip', label: 'Flip', tokens: ['fa-flip'] },
    { id: 'shake', label: 'Shake', tokens: ['fa-shake'] },
];

const SIZE_TOKENS = new Set([
    'site-icon-size-x-small',
    'site-icon-size-small',
    'site-icon-size-medium',
    'site-icon-size-large',
    'site-icon-size-x-large',
]);

function stripSizeTokens(className = '') {
    return String(className)
        .split(/\s+/)
        .filter((token) => token && !SIZE_TOKENS.has(token))
        .join(' ');
}

function getAnimationMeta(id) {
    return (
        ANIMATION_OPTIONS.find((opt) => opt.id === id) || {
            id: '',
            label: 'None',
            tokens: [],
        }
    );
}

const PACK_STYLE_OPTIONS = ICON_PACKS.reduce((acc, pack) => {
    if (pack.key === 'custom') {
        acc[pack.key] = [];
    } else if (pack.key === 'brands') {
        acc[pack.key] = [{ key: 'brands', label: STYLE_LABELS.brands }];
    } else {
        acc[pack.key] = [
            { key: 'solid', label: STYLE_LABELS.solid },
            { key: 'regular', label: STYLE_LABELS.regular },
            { key: 'light', label: STYLE_LABELS.light },
            { key: 'thin', label: STYLE_LABELS.thin },
        ];
    }
    return acc;
}, {});

function isCustomIconValue(value) {
    const normalized = String(value || '').trim();
    if (!normalized) return false;
    return (
        /^(https?:\/\/|\/|data:image\/)/i.test(normalized) ||
        /<\s*(svg|img|picture|iframe|script|div|span)[\s>]/i.test(normalized)
    );
}

const EXTRA_TERMS = {
    'wand-magic-sparkles': ['sparkle', 'sparkles'],
    'wand-sparkles': ['sparkle', 'sparkles'],
    'spray-can-sparkles': ['sparkle', 'sparkles'],
    'hand-sparkles': ['sparkle', 'sparkles'],
};

const BASE_ICONS = ICON_DATA.map((icon) => {
    const baseTerms = icon.terms || [];
    const extra = EXTRA_TERMS[icon.name] || [];
    const slug = icon.name || '';
    const slugWords = slug.split('-');
    const compact = slug.replace(/-/g, '');
    const labelWords = (icon.label || '')
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);
    const enrichedTerms = new Set([
        ...baseTerms,
        ...extra,
        slug,
        slug.replace(/-/g, ' '),
        compact,
        ...slugWords,
        ...labelWords,
    ]);
    return {
        ...icon,
        terms: Array.from(enrichedTerms),
    };
});

const ICONS = BASE_ICONS;
const ICON_MAP = new Map(ICONS.map((icon) => [icon.name, icon]));
const KIT_ICONS = new Set([
    'angi',
    'bbb',
    'capterra',
    'cash',
    'homestars',
    'mobilo',
    'nextdoor',
    'nicejob',
    'thumbtack',
    'yellowpages',
    'zelle',
]);

const INDUSTRIES = [
    {
        key: 'appliance-repair',
        label: 'Appliance Repair',
        icons: [
            'screwdriver-wrench',
            'toolbox',
            'wrench',
            'gears',
            'plug',
            'outlet',
            'plug-circle-bolt',
            'plug-circle-check',
            'plug-circle-exclamation',
            'plug-circle-minus',
            'plug-circle-plus',
            'plug-circle-xmark',
            'microwave',
            'refrigerator',
            'washing-machine',
            'temperature-half',
            'battery-three-quarters',
            'battery-half',
            'battery-empty',
            'truck-fast',
            'clock',
            'oven',
            'dryer',
        ],
    },
    {
        key: 'automotive',
        label: 'Automotive',
        icons: [
            'car',
            'car-wrench',
            'truck',
            'engine',
            'oil-can',
            'gas-pump',
            'tire',
            'gauge-high',
            'battery-bolt',
            'road',
            'car-battery',
            'car-burst',
            'car-rear',
            'car-side',
            'car-tunnel',
            'truck-monster',
            'truck-pickup',
            'certificate',
            'wrench',
            'car-tilt',
            'car-crash',
            'truck-front',
            'truck-fast',
            'gear',
            'screwdriver-wrench',
            'award',
            'shield-halved',
        ],
    },
    {
        key: 'carpet-cleaning',
        label: 'Carpet Cleaning',
        icons: [
            'rug',
            'broom',
            'spray-can-sparkles',
            'soap',

            'pump-soap',
            'jug-detergent',

            'bath',
            'shower',
            'droplet',
            'wind',
            'vacuum',
            'vacuum-robot',
            'stars',
            'sparkles',
            'couch',
            'house',
            'square',
            'leaf',
            'building',
        ],
    },
    {
        key: 'construction-renovation',
        label: 'Construction & Renovation',
        icons: [
            'hammer',
            'helmet-safety',
            'person-digging',
            'toolbox',
            'ruler-combined',
            'screwdriver-wrench',
            'trowel-bricks',
            'trowel',
            'screwdriver',
            'ruler-horizontal',
            'ruler-vertical',
            'house-chimney',
            'house-circle-check',
            'layer-group',
            'compass-drafting',
            'shield-check',
            'shield-haled',
            'award',
            'ruler-triangle',
            'check',
            'fence',
        ],
    },
    {
        key: 'electrical-services',
        label: 'Electrical Services',
        icons: [
            'bolt',
            'plug',
            'outlet',
            'lightbulb',
            'charging-station',
            'battery-three-quarters',
            'battery-half',
            'battery-empty',
            'car-battery',
            'plug-circle-bolt',
            'plug-circle-check',
            'plug-circle-exclamation',
            'battery-full',
            'solar-panel',
            'tower-broadcast',

            'meter',
            'transformer-bolt',
            'panel-ews',
            'shield-halved',
            'shield-check',
        ],
    },
    {
        key: 'gutter-cleaning',
        label: 'Gutter Cleaning',
        icons: [
            'house-chimney',
            'cloud-rain',
            'water',
            'droplet',
            'leaf',
            'person-shelter',
            'house-flood-water',
            'arrows-down-to-line',
            'down-from-line',
            'rectangle-vertical-history',
            'cloud-showers-water',
            'arrow-up-from-water-pump',
            'cloud-bolt',
            'cloud-moon-rain',
            'cloud-showers-heavy',
            'cloud-sun-rain',
            'umbrella',
            'raindrops',
            'check',
            'house-circle-check',
        ],
    },
    {
        key: 'health-personal-care',
        label: 'Health & Personal Care',
        icons: [
            'heart-pulse',
            'stethoscope',
            'hand-holding-heart',
            'notes-medical',
            'user-doctor',
            'kit-medical',
            'spa',
            'hands-bubbles',
            'person',
            'face-smile',
            'briefcase-medical',
            'hand-holding-medical',
            'hospital-user',
            'house-medical',
            'staff-snake',
            'star-of-life',
            'user-nurse',
            'heart',
            'face-relaxed',
            'shield-heart',
            'sparkles',
            'handshake-angle',
            'circle-check',
        ],
    },
    {
        key: 'home-business-cleaning',
        label: 'Home & Business Cleaning',
        icons: [
            'spray-can-sparkles',
            'broom',
            'soap',

            'pump-soap',
            'jug-detergent',
            'vacuum-robot',

            'shower',
            'pump-medical',
            'bath',
            'bucket',
            'building',
            'house',
            'hands-bubbles',
            'shield-heart',
            'sparkles',
            'hand-sparkles',
            'calendar',
            'vacuum',
            'shield-check',
            'badge-check',
        ],
    },
    {
        key: 'hvac-generator',
        label: 'HVAC & Generator Services',
        icons: [
            'fan',
            'snowflake',
            'temperature-high',
            'temperature-low',
            'wind',
            'house-signal',
            'bolt',
            'battery-bolt',
            'engine',
            'plug-circle-bolt',
            'temperature-arrow-down',
            'temperature-full',
            'temperature-quarter',
            'fire',
            'fire-flame-curved',
            'icicles',
            'air-conditioner',
            'temperature-empty',
            'temperature-arrow-up',
            'screwdriver-wrench',
            'users',
            'shield-check',
            'calendar',
        ],
    },
    {
        key: 'inspection',
        label: 'Inspection',
        icons: [
            'magnifying-glass',
            'magnifying-glass-chart',
            'clipboard-list',
            'clipboard-check',
            'file-circle-check',
            'badge-check',
            'circle-check',
            'ruler',
            'camera',
            'triangle-exclamation',
            'bars-progress',
            'binoculars',
            'building-circle-check',
            'book',
            'book-bookmark',
            'book-open',
            'certificate',
            'bullseye',
            'shield-check',
            'map-location-dot',
        ],
    },
    {
        key: 'it-technology',
        label: 'IT & Technology Services',
        icons: [
            'laptop',
            'computer',
            'server',
            'network-wired',
            'wifi',
            'cloud',
            'microchip',
            'shield-halved',
            'lock',
            'gear',
            'laptop-code',
            'code',
            'code-branch',
            'cloud-arrow-up',
            'cloud-arrow-down',
            'circle-nodes',
            'shield',
            'barcode',
            'bolt',
            'badge-check',
            'laptop-mobile',
            'mobile-screen',
            'screwdriver-wrench',
        ],
    },
    {
        key: 'junk-removal',
        label: 'Junk Removal',
        icons: [
            'dumpster',
            'box',
            'box-archive',
            'boxes-packing',
            'boxes-stacked',
            'pallet',
            'dumpster-fire',
            'trash',
            'trash-can',
            'recycle',
            'box-open',
            'truck',
            'hand-holding-box',
            'house-circle-xmark',
            'broom',
            'circle-arrow-up-right',
            'couch',
            'box-open-full',
            'trash',
            'tv',
            'refrigerator',
            'circle-check',
        ],
    },
    {
        key: 'landscaping-garden',
        label: 'Landscaping & Garden',
        icons: [
            'tree',
            'leaf',
            'seedling',

            'plant-wilt',
            'sun',
            'droplet',
            'mountain-sun',
            'fence',
            'tractor',
            'clover',
            'cloud-sun',
            'cloud-sun-rain',
            'faucet',
            'faucet-drip',
            'arrow-up-from-water-pump',
            'sprinkler',
            'flower',
            'flower-tulip',
            'water',
            'shield-check',
        ],
    },
    {
        key: 'legal-financial',
        label: 'Legal & Financial Services',
        icons: [
            'scale-balanced',
            'gavel',
            'file-contract',
            'handshake',
            'briefcase',
            'building-columns',
            'calculator',
            'chart-line',
            'money-check-dollar',
            'shield-check',
            'money-bill-transfer',
            'money-bill-trend-up',
            'money-check',
            'chart-pie',
            'chart-simple',
            'coins',
            'comment-dollar',
            'vault',
            'file-signature',
            'calendar-check',
            'dollar',
            'laptop',
            'circle-check',
        ],
    },
    {
        key: 'moving-services',
        label: 'Moving Services',
        icons: [
            'truck-moving',
            'box',
            'boxes-stacked',
            'people-carry-box',
            'dolly',
            'tape',
            'warehouse',
            'house-circle-check',
            'route',
            'arrows-left-right',
            'cart-flatbed',
            'pallet',
            'hand-holding-box',
            'map',
            'map-location',
            'map-location-dot',
            'person-walking-luggage',
            'box-archive',
            'people-carry',
            'box-open-full',
            'boxes-packing',
            'box-open',
            'truck-ramp-box',
            'couch',
            'piano',
            'shield-check',
        ],
    },
    {
        key: 'painting-services',
        label: 'Painting Services',
        icons: [
            'brush',
            'paint-roller',
            'palette',
            'fill-drip',
            'droplet',
            'tape',
            'swatchbook',
            'ruler-combined',
            'sparkles',
            'house',
            'fill',
            'paintbrush',
            'eye-dropper',
            'spray-can',
            'droplet-slash',
            'circle-half-stroke',
            'bezier-curve',
            'clone',
            'shield-check',
        ],
    },
    {
        key: 'pest-control',
        label: 'Pest Control',
        icons: [
            'spider',
            'mosquito',
            'shield',
            'ban-bug',
            'spray-can',
            'triangle-exclamation',
            'skull-crossbones',
            'house-circle-exclamation',
            'shield-virus',
            'ban',
            'building-shield',
            'file-shield',
            'ant',
            'bugs',
            'bee',
            'bug-slash',
            'mouse-field',
            'leaf',
            'shield-check',
        ],
    },
    {
        key: 'pet-care',
        label: 'Pet Care',
        icons: [
            'paw',
            'dog',
            'cat',
            'bone',
            'shield-dog',
            'heart',
            'house',
            'scissors',
            'bath',
            'calendar-check',
            'shield-cat',
            'bowl-food',
            'house-chimney-user',
            'id-badge',
            'id-card-clip',
            'dog-leashed',
            'leaf',
            'shield-check',
            'sparkles',
        ],
    },
    {
        key: 'plumbing-services',
        label: 'Plumbing Services',
        icons: [
            'faucet',
            'faucet-drip',
            'pipe',
            'toilet',
            'shower',
            'water',
            'droplet',
            'temperature-half',
            'wrench',
            'screwdriver-wrench',
            'arrow-up-from-water-pump',
            'bath',
            'bottle-water',
            'gauge',
            'sink',
            'pipe-circle-check',
            'grate-droplet',
            'clock',
            'video',
            'award',
            'truck-fast',
        ],
    },
    {
        key: 'pool-services',
        label: 'Pool Services',
        icons: [
            'person-swimming',
            'water',
            'droplet',
            'wave-square',
            'life-ring',
            'sun',
            'umbrella-beach',
            'gauge',
            'filter',
            'pump-soap',
            'shower',
            'cloud-showers-water',
            'cloud-sun',
            'cloud-sun-rain',
            'house-tsunami',
            'swimmer',
            'water-ladder',
            'flask',
        ],
    },
    {
        key: 'pressure-washing',
        label: 'Pressure Washing',
        icons: [
            'spray-can',
            'spray-can-sparkles',
            'water',
            'droplet',
            'burst',
            'tornado',
            'house-chimney',
            'border-all',
            'wand-magic-sparkles',
            'shield-check',
            'cloud-showers-water',
            'faucet',
            'faucet-drip',
            'cloud-bolt',
            'bolt-lightning',
            'cloud',
            'hand-sparkles',
            'house-building',
            'house',
            'building',
            'leaf',
            'fence',
            'hose',
            'chimney',
            'solar-panel',
        ],
    },
    {
        key: 'window-cleaning',
        label: 'Window Cleaning',
        icons: [
            'spray-can-sparkles',
            'droplet',
            'sun',
            'cloud',

            'broom',
            'hand-sparkles',
            'house',
            'house-chimney-window',

            'vacuum',
            'vacuum-robot',
            'archway',
            'city',
            'window-frame',
            'window-frame-open',
            'sparkles',
            'leaf',
        ],
    },
];

const GENERAL_GROUPS = [
    {
        key: 'badges',
        label: 'Badges',
        icons: [
            'award',
            'certificate',
            'medal',
            'trophy',
            'shield-check',
            'badge-check',
            'ribbon',
            'crown',
            'shield',
            'shield-halved',
        ],
    },
    {
        key: 'usps',
        label: 'USPs',
        icons: [
            'sparkles',
            'thumbs-up',
            'trophy',
            'shield-check',
            'shield-halved',
            'shield',
            'lock',
            'key',
            'handshake',
            'heart',
            'badge-check',
            'award',
            'medal',
            'shield-heart',
            'handshake-angle',
            'star',
        ],
    },
    {
        key: 'arrows',
        label: 'Arrows',
        icons: [
            'arrow-right',
            'arrow-left',
            'arrow-up',
            'arrow-down',
            'arrow-up-right-from-square',
            'arrow-up-right',
            'arrow-up-left',
            'arrow-down-right',
            'arrow-down-left',
            'angles-right',
            'angles-left',
            'angles-up',
            'angles-down',
            'chevron-right',
            'chevron-left',
            'chevron-up',
            'chevron-down',
            'caret-right',
            'caret-left',
            'caret-up',
            'caret-down',
            'right-long',
            'left-long',
            'up-long',
            'down-long',
            'location-arrow',
            'share',
            'arrow-pointer',
            'arrow-right-arrow-left',
            'arrow-up-right-dots',
            'arrow-trend-up',
            'arrow-trend-down',
            'arrow-up-from-bracket',
            'arrow-right-from-bracket',
            'arrow-rotate-right',
            'arrow-rotate-left',
            'arrow-up-from-water-pump',
            'arrows-rotate',
        ],
    },
    {
        key: 'communication',
        label: 'Communication',
        icons: [
            'phone',
            'phone-volume',
            'mobile-screen',
            'envelope',
            'at',
            'message',
            'message-lines',
            'comment-dots',
            'comments',
            'paper-plane',
            'inbox',
            'phone-flip',
            'square-phone',
            'square-phone-flip',
            'headset',
            'voicemail',
            'comment',
            'envelope-open',
            'envelope-open-text',
        ],
    },
    {
        key: 'status',
        label: 'Status',
        icons: [
            'circle-check',
            'check',
            'triangle-exclamation',
            'circle-exclamation',
            'clock',
            'circle-info',
            'circle-xmark',
            'xmark',
            'circle-question',
            'circle-minus',
            'circle-plus',
            'circle-pause',
            'hourglass-half',
            'hourglass-start',
            'hourglass-end',
        ],
    },
    {
        key: 'share',
        label: 'Share',
        icons: [
            'share',
            'share-from-square',
            'share-nodes',
            'bullhorn',
            'paper-plane',
            'megaphone',
            'message',
            'message-lines',
            'comments',
            'comment-dots',
            'volume-high',
            'user-group',
            'people-group',
            'arrow-up-from-bracket',
            'link',
            'copy',
            'paperclip',
        ],
    },
];

function normalizeStyleForPack(style, pack) {
    const mapped = STYLE_ALIASES[style] || style;
    const opts = PACK_STYLE_OPTIONS[pack] || [];
    if (opts.some((opt) => opt.key === style)) return style;
    if (opts.some((opt) => opt.key === mapped)) return mapped;
    return opts[0]?.key || 'solid';
}

function parseIconValue(value) {
    const tokens = String(value || '')
        .split(/\s+/)
        .filter(Boolean);
    const pack =
        tokens.includes('fa-sharp-duotone') ||
        (tokens.includes('fa-sharp') && tokens.includes('fa-duotone'))
            ? 'sharp-duotone'
            : tokens.includes('fa-sharp')
            ? 'sharp'
            : tokens.includes('fa-duotone')
            ? 'duotone'
            : tokens.includes('fa-brands') || tokens.includes('fa-kit')
            ? 'brands'
            : 'classic';

    const styleToken = tokens.find((t) => STYLE_TOKEN_MAP[t]);
    const style = styleToken ? STYLE_TOKEN_MAP[styleToken] : 'solid';

    const durationToken = tokens.find((t) => /^fa-duration-(\d+)/.test(t));
    const durationMs = durationToken
        ? Number(durationToken.replace('fa-duration-', ''))
        : null;

    const animationId =
        ANIMATION_OPTIONS.filter((opt) => opt.id)
            .sort((a, b) => b.tokens.length - a.tokens.length)
            .find(
                (opt) =>
                    opt.tokens.length &&
                    opt.tokens.every((t) => tokens.includes(t))
            )?.id || '';
    const animateOnce = tokens.includes('fa-once');
    const sizeToken = tokens.find((t) => SIZE_TOKENS.has(t)) || '';

    const reserved = new Set([
        'fa-sharp',
        'fa-duotone',
        'fa-sharp-duotone',
        'fa-once',
        'fa-kit',
        ...Object.keys(STYLE_TOKEN_MAP),
        ...Object.values(PACK_TOKEN).flat(),
        ...ANIMATION_AND_UTILITY,
        ...SIZE_TOKENS,
    ]);
    if (durationToken) reserved.add(durationToken);
    tokens.forEach((t) => {
        if (t.startsWith(RESTART_PREFIX)) reserved.add(t);
    });

    const iconToken = tokens.find(
        (t) =>
            t.startsWith('fa-') &&
            !reserved.has(t) &&
            !/^fa-duration-\d+/.test(t)
    );
    const iconName = iconToken ? iconToken.replace(/^fa-/, '') : '';
    const extraTokens = tokens
        .filter(
            (t) =>
                t !== iconToken &&
                !reserved.has(t) &&
                !/^fa-duration-\d+/.test(t)
        )
        .join(' ')
        .trim();

    return {
        pack,
        style,
        iconName,
        extraTokens,
        animation: animationId,
        animateOnce,
        durationMs,
        sizeToken,
    };
}

function buildIconValue(
    iconName,
    pack,
    style,
    extraTokens,
    animationId,
    durationMs,
    loopEnabled,
    restartKey = null,
    sizeToken = ''
) {
    const tokens = [];
    const normalizedPack = pack || 'classic';
    const normalizedStyle = normalizeStyleForPack(style, normalizedPack);

    const packToUse = normalizedPack;

    if (packToUse === 'brands') {
        tokens.push(KIT_ICONS.has(iconName) ? 'fa-kit' : 'fa-brands');
    } else {
        (PACK_TOKEN[packToUse] || []).forEach((t) => tokens.push(t));
    }

    if (packToUse === 'brands') {
        // brands has its own pack token, no style token
    } else {
        tokens.push(`fa-${normalizedStyle}`);
    }

    const animTokens = getAnimationMeta(animationId).tokens || [];
    tokens.push(...animTokens);
    if (sizeToken) tokens.push(sizeToken);
    if (animationId && restartKey !== null) {
        tokens.push(`${RESTART_PREFIX}${restartKey}`);
    }
    if (animationId && typeof durationMs === 'number' && durationMs > 0) {
        const clamped = Math.max(1000, Math.min(3000, Math.round(durationMs)));
        tokens.push(`fa-duration-${clamped}`);
    }
    if (!loopEnabled) tokens.push('fa-once');

    if (iconName) tokens.push(`fa-${iconName}`);
    if (extraTokens) tokens.push(extraTokens);

    const deduped = Array.from(new Set(tokens.filter(Boolean)));
    return deduped.join(' ').replace(/\s+/g, ' ').trim();
}

function normalizeClassTokens(str) {
    const tokens = String(str || '')
        .split(/\s+/)
        .filter(Boolean);
    const deduped = Array.from(new Set(tokens));
    deduped.sort();
    return deduped.join(' ');
}

function bestStyleForIcon(icon, pack, desiredStyle) {
    const normalized = normalizeStyleForPack(desiredStyle, pack);
    if (!icon || !Array.isArray(icon.variants) || icon.variants.length === 0) {
        return normalized;
    }

    if (pack === 'brands') return 'brands';

    const availableStyles = new Set(icon.variants.map((v) => v.style));
    if (availableStyles.has(normalized)) return normalized;
    if (availableStyles.has('solid')) return 'solid';
    if (availableStyles.has('regular')) return 'regular';

    const fallback = PACK_STYLE_OPTIONS[pack]?.find((opt) =>
        availableStyles.has(opt.key)
    );
    return fallback?.key || normalized;
}

function LoopToggle({ checked, onChange, ariaLabel }) {
    return (
        <label className="relative inline-flex cursor-pointer items-center">
            <input
                type="checkbox"
                className="peer sr-only"
                checked={checked}
                onChange={(e) => onChange?.(e.target.checked)}
                aria-label={ariaLabel}
            />
            <div className="peer h-5 w-9 rounded-full bg-neutral-300 after:absolute after:left-[3px] after:top-0.5 after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-4 peer-checked:after:border-white peer-focus:ring-1 peer-focus:ring-blue-300" />
        </label>
    );
}

export default function FontAwesomeIconPicker({
    value,
    onChange,
    placeholder = 'Search icons…',
    collapseByDefault = true,
}) {
    const editor = useEditor();
    const initialValue = typeof value === 'string' ? value.trim() : '';
    const [search, setSearch] = React.useState('');
    const [selectedPack, setSelectedPack] = React.useState(() =>
        isCustomIconValue(initialValue) ? 'custom' : 'classic'
    );
    const [selectedStyle, setSelectedStyle] = React.useState(
        normalizeStyleForPack('solid', 'classic')
    );
    const [selectedCategory, setSelectedCategory] = React.useState('all');
    const [manualValue, setManualValue] = React.useState(value || '');
    const [showAll, setShowAll] = React.useState(false);
    const [selectedAnimation, setSelectedAnimation] = React.useState('');
    const [loopEnabled, setLoopEnabled] = React.useState(true);
    const [durationMs, setDurationMs] = React.useState(null);
    const [animationRestartKey, setAnimationRestartKey] = React.useState(0);
    const [visibleCount, setVisibleCount] = React.useState(100);
    const [isExpanded, setIsExpanded] = React.useState(
        initialValue ? false : !collapseByDefault
    );
    const lastEmittedRef = React.useRef('');
    const prevSearchRef = React.useRef('');
    const syncLockRef = React.useRef(false);
    const customUploadInputRef = React.useRef(null);
    const dispatchRestart = React.useCallback((detail, delay = 80) => {
        if (typeof window === 'undefined') return;
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('fa-restart', { detail }));
        }, delay);
    }, []);

    const incomingValue = React.useMemo(
        () => (typeof value === 'string' ? value.trim() : ''),
        [value]
    );
    const incomingNormalized = React.useMemo(
        () => normalizeClassTokens(incomingValue),
        [incomingValue]
    );
    const manualNormalized = React.useMemo(
        () => normalizeClassTokens(manualValue),
        [manualValue]
    );

    React.useEffect(() => {
        if (incomingNormalized !== manualNormalized) {
            setManualValue(stripRestartTokens(incomingValue));
            setShowAll(false);
        }
        if (!incomingValue) {
            setIsExpanded(!collapseByDefault);
        }
        lastEmittedRef.current = incomingNormalized;
    }, [
        collapseByDefault,
        incomingValue,
        incomingNormalized,
        manualNormalized,
    ]);

    const parsed = React.useMemo(
        () => parseIconValue(manualValue),
        [manualValue]
    );

    React.useEffect(() => {
        if (syncLockRef.current) {
            syncLockRef.current = false;
            return;
        }
        // Avoid resetting user-selected pack/style when the field is cleared
        if (!manualValue.trim()) return;
        if (isCustomIconValue(manualValue)) {
            if (selectedPack !== 'custom') setSelectedPack('custom');
            return;
        }
        if (selectedPack === 'custom') return;
        const nextPack = parsed.pack || 'classic';
        if (nextPack !== selectedPack) setSelectedPack(nextPack);
        const nextStyle = normalizeStyleForPack(parsed.style, nextPack);
        if (nextStyle && nextStyle !== selectedStyle)
            setSelectedStyle(nextStyle);
        if ((parsed.animation || '') !== selectedAnimation) {
            setSelectedAnimation(parsed.animation || '');
        }
        const nextLoop = !parsed.animateOnce;
        if (nextLoop !== loopEnabled) setLoopEnabled(nextLoop);
        const incomingDuration = parsed.durationMs ?? null;
        if (incomingDuration !== durationMs) setDurationMs(incomingDuration);
    }, [
        manualValue,
        parsed.pack,
        parsed.style,
        parsed.animation,
        parsed.animateOnce,
        parsed.durationMs,
        selectedPack,
        selectedStyle,
        selectedAnimation,
        loopEnabled,
        durationMs,
    ]);

    const currentIconMeta = parsed.iconName
        ? ICON_MAP.get(parsed.iconName)
        : null;
    const isBrandOnlyIcon =
        currentIconMeta &&
        Array.isArray(currentIconMeta.variants) &&
        currentIconMeta.variants.length > 0 &&
        currentIconMeta.variants.every((v) => v.style === 'brands');

    const styleOptions = isBrandOnlyIcon
        ? [{ key: 'brands', label: 'Brands' }]
        : PACK_STYLE_OPTIONS[selectedPack] || [];

    const filteredIcons = React.useMemo(() => {
        const term = search.trim().toLowerCase();
        return ICONS.filter((icon) => {
            const variants = icon.variants || [];
            const isBrandsOnly =
                variants.length > 0 &&
                variants.every((v) => v.style === 'brands');

            // When not searching, honor the selected pack (brands vs others).
            if (!term) {
                if (selectedPack === 'brands') {
                    if (!variants.some((v) => v.style === 'brands'))
                        return false;
                } else if (isBrandsOnly) {
                    return false;
                }
            }

            // When searching, always include brands and non-brands in results.
            // This avoids being stuck in brands-only search after picking a brand icon.

            if (selectedCategory !== 'all') {
                const industry = INDUSTRIES.find(
                    (ind) => ind.key === selectedCategory
                );
                const general = GENERAL_GROUPS.find(
                    (g) => g.key === selectedCategory
                );
                const allowed = industry?.icons || general?.icons || [];
                if (!allowed.includes(icon.name)) return false;
            }

            if (!term) return true;
            const haystack = [icon.label, icon.name, ...(icon.terms || [])]
                .join(' ')
                .toLowerCase();
            return haystack.includes(term);
        });
    }, [search, selectedPack, selectedCategory, showAll]);

    React.useEffect(() => {
        if (search !== prevSearchRef.current) {
            // When typing again after selecting a brand icon, go back to a regular pack for searching.
            if (selectedPack === 'brands') {
                setSelectedPack('classic');
                setSelectedStyle(normalizeStyleForPack('solid', 'classic'));
            }
            prevSearchRef.current = search;
        }
    }, [search, selectedPack]);

    const showSelectedOnly =
        !!parsed.iconName &&
        !showAll &&
        search.trim().length === 0 &&
        selectedCategory === 'all';
    const selectedIconEntry = React.useMemo(() => {
        if (!parsed.iconName) return null;
        const fromMap = ICON_MAP.get(parsed.iconName);
        if (fromMap) return fromMap;
        return {
            name: parsed.iconName,
            label: parsed.iconName,
            variants: [],
            __synthetic: true,
        };
    }, [parsed.iconName]);
    const shouldRenderGrid =
        showAll ||
        search.trim().length > 0 ||
        selectedCategory !== 'all' ||
        showSelectedOnly;

    const limitedIcons = (() => {
        if (showSelectedOnly) {
            return selectedIconEntry ? [selectedIconEntry] : [];
        }
        return shouldRenderGrid ? filteredIcons.slice(0, visibleCount) : [];
    })();

    React.useEffect(() => {
        setVisibleCount(100);
    }, [search, selectedPack, selectedStyle, selectedCategory, showAll]);

    const handleLoadMore = React.useCallback(() => {
        if (!shouldRenderGrid) return;
        setVisibleCount((prev) => Math.min(filteredIcons.length, prev + 100));
    }, [filteredIcons.length, shouldRenderGrid]);

    const handleScroll = (event) => {
        const el = event.currentTarget;
        if (!el) return;
        const nearBottom =
            el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
        if (nearBottom) handleLoadMore();
    };

    const handleIconSelect = (icon) => {
        const isBrandIcon =
            icon?.variants?.length &&
            icon.variants.every((v) => v.style === 'brands');
        const basePack =
            selectedPack === 'brands' && !isBrandIcon
                ? 'classic'
                : selectedPack;
        const nextPack = isBrandIcon ? 'brands' : basePack;
        const styleForIcon = bestStyleForIcon(
            icon,
            nextPack,
            isBrandIcon ? 'brands' : selectedStyle
        );
        const nextRestartKey = selectedAnimation
            ? animationRestartKey + 1
            : animationRestartKey;
        const emitValue = buildIconValue(
            icon.name,
            nextPack,
            styleForIcon,
            parsed.extraTokens,
            selectedAnimation,
            selectedAnimation && durationMs !== null ? durationMs : null,
            loopEnabled,
            selectedAnimation ? nextRestartKey : null
        );
        const displayValue = buildIconValue(
            icon.name,
            nextPack,
            styleForIcon,
            parsed.extraTokens,
            selectedAnimation,
            selectedAnimation && durationMs !== null ? durationMs : null,
            loopEnabled,
            null
        );
        const isCurrentSelection =
            parsed.iconName === icon.name &&
            parsed.pack === nextPack &&
            normalizeStyleForPack(parsed.style, parsed.pack) === styleForIcon;
        if (isCurrentSelection) {
            lastEmittedRef.current = '';
            setManualValue('');
            onChange?.('');
            return;
        }
        setManualValue(displayValue);
        setAnimationRestartKey(nextRestartKey);
        if (isBrandIcon) {
            setSelectedPack('brands');
            setSelectedStyle('brands');
        } else if (selectedPack === 'brands') {
            setSelectedPack('classic');
            setSelectedStyle(normalizeStyleForPack('solid', 'classic'));
        }
        onChange?.(emitValue);
        if (selectedAnimation) {
            dispatchRestart({
                iconName: icon.name,
                className: stripRestartTokens(emitValue),
            });
        }
    };

    const handlePackChange = (nextPack) => {
        if (nextPack === 'custom') {
            setSelectedPack('custom');
            setSearch('');
            setSelectedCategory('all');
            setShowAll(false);
            return;
        }
        if (selectedPack === 'custom') {
            syncLockRef.current = true;
            setSelectedPack(nextPack);
            setSelectedStyle(normalizeStyleForPack(selectedStyle, nextPack));
            if (isCustomIconValue(manualValue)) {
                setManualValue('');
                onChange?.('');
            }
            return;
        }
        const currentIcon = parsed.iconName && ICON_MAP.get(parsed.iconName);
        const brandOnlyCurrent =
            currentIcon &&
            currentIcon.variants?.every((v) => v.style === 'brands');
        if (brandOnlyCurrent && nextPack !== 'brands') {
            setSelectedPack('brands');
            return;
        }
        setSelectedPack(nextPack);
        const normalizedStyle = normalizeStyleForPack(selectedStyle, nextPack);
        setSelectedStyle(normalizedStyle);
        const currentIsBrand = currentIcon?.variants?.some(
            (v) => v.style === 'brands'
        );
        const nextIsBrandsPack = nextPack === 'brands';
        const shouldClear =
            (nextIsBrandsPack && !currentIsBrand) ||
            (!nextIsBrandsPack && currentIsBrand);

        if (shouldClear) {
            setManualValue('');
            setShowAll(nextIsBrandsPack ? true : showAll);
            setSearch('');
            setSelectedCategory('all');
            onChange?.('');
            return;
        }
        if (nextPack === 'brands') {
            setShowAll(true);
            setSearch('');
            setSelectedCategory('all');
        }

        if (!parsed.iconName) return;
        const effectiveStyle = bestStyleForIcon(
            currentIcon,
            nextPack,
            normalizedStyle
        );
        const next = buildIconValue(
            parsed.iconName,
            nextPack,
            effectiveStyle,
            parsed.extraTokens,
            selectedAnimation,
            selectedAnimation && durationMs !== null ? durationMs : null,
            loopEnabled,
            null
        );
        const nextNorm = normalizeClassTokens(next);
        if (
            nextNorm === manualNormalized ||
            nextNorm === incomingNormalized ||
            nextNorm === lastEmittedRef.current
        )
            return;
        lastEmittedRef.current = nextNorm;
        setManualValue(next);
        onChange?.(next);
    };

    const handleStyleChange = (nextStyle) => {
        const currentIcon = parsed.iconName && ICON_MAP.get(parsed.iconName);
        const brandOnlyCurrent =
            currentIcon &&
            currentIcon.variants?.every((v) => v.style === 'brands');
        if (brandOnlyCurrent) return;
        setSelectedStyle(nextStyle);
        if (!parsed.iconName) return;
        const effectiveStyle = bestStyleForIcon(
            currentIcon,
            selectedPack,
            nextStyle
        );
        const next = buildIconValue(
            parsed.iconName,
            selectedPack,
            effectiveStyle,
            parsed.extraTokens,
            selectedAnimation,
            selectedAnimation && durationMs !== null ? durationMs : null,
            loopEnabled,
            null
        );
        const nextNorm = normalizeClassTokens(next);
        if (
            nextNorm === manualNormalized ||
            nextNorm === incomingNormalized ||
            nextNorm === lastEmittedRef.current
        )
            return;
        lastEmittedRef.current = nextNorm;
        setManualValue(next);
        onChange?.(next);
    };

    const handleAnimationChange = (nextAnimationId) => {
        setSelectedAnimation(nextAnimationId);
        const nextLoop = nextAnimationId ? false : true;
        setLoopEnabled(nextLoop);
        if (!parsed.iconName) {
            return;
        }
        const currentIcon = ICON_MAP.get(parsed.iconName);
        const effectiveStyle = bestStyleForIcon(
            currentIcon,
            selectedPack,
            selectedStyle
        );
        const animMeta = getAnimationMeta(nextAnimationId);
        const next = buildIconValue(
            parsed.iconName,
            selectedPack,
            effectiveStyle,
            parsed.extraTokens,
            animMeta.id,
            animMeta.id && durationMs !== null ? durationMs : null,
            nextLoop,
            null
        );
        const nextNorm = normalizeClassTokens(next);
        setManualValue(next);
        if (nextNorm !== lastEmittedRef.current) {
            lastEmittedRef.current = nextNorm;
            onChange?.(next);
        }
    };

    const handleLoopToggle = (forcedNext) => {
        if (!selectedAnimation) return;
        const nextLoop =
            typeof forcedNext === 'boolean' ? forcedNext : !loopEnabled;
        setLoopEnabled(nextLoop);
        if (!parsed.iconName) return;
        const currentIcon = ICON_MAP.get(parsed.iconName);
        const effectiveStyle = bestStyleForIcon(
            currentIcon,
            selectedPack,
            selectedStyle
        );
        const next = buildIconValue(
            parsed.iconName,
            selectedPack,
            effectiveStyle,
            parsed.extraTokens,
            selectedAnimation,
            selectedAnimation && durationMs !== null ? durationMs : null,
            nextLoop,
            null
        );
        const nextNorm = normalizeClassTokens(next);
        setManualValue(next);
        if (nextNorm !== lastEmittedRef.current) {
            lastEmittedRef.current = nextNorm;
            onChange?.(next);
        }
    };

    const handleDurationChange = (nextSeconds) => {
        const nextMsRaw = Math.round(nextSeconds * 1000);
        const nextMs =
            !selectedAnimation || nextMsRaw <= 0
                ? null
                : Math.max(1000, Math.min(3000, nextMsRaw));
        setDurationMs(nextMs);
        if (!parsed.iconName) return;
        if (!selectedAnimation) return;
        const currentIcon = ICON_MAP.get(parsed.iconName);
        const effectiveStyle = bestStyleForIcon(
            currentIcon,
            selectedPack,
            selectedStyle
        );
        const next = buildIconValue(
            parsed.iconName,
            selectedPack,
            effectiveStyle,
            parsed.extraTokens,
            selectedAnimation,
            nextMs,
            loopEnabled,
            null
        );
        const nextNorm = normalizeClassTokens(next);
        setManualValue(next);
        if (nextNorm !== lastEmittedRef.current) {
            lastEmittedRef.current = nextNorm;
            onChange?.(next);
        }
    };

    const packOptions = ICON_PACKS;
    const isCustomPack = selectedPack === 'custom';
    const customValue = isCustomIconValue(manualValue) ? manualValue : '';
    const companyId =
        editor.global?.data?.company_id || editor.site?.data?.company_id;
    const handleCustomValueChange = (nextValue) => {
        const normalized = String(nextValue || '');
        lastEmittedRef.current = normalizeClassTokens(normalized);
        setManualValue(normalized);
        onChange?.(normalized);
    };

    const guessedIconName = search.trim().replace(/\s+/g, '-');
    const showFallbackTile =
        filteredIcons.length === 0 && guessedIconName.length > 0;
    const durationDisplayMs = durationMs ?? 1000;
    const packLabel = isCustomPack
        ? 'Custom'
        : ICON_PACKS.find((pack) => pack.key === parsed.pack)?.label ||
          'Classic';
    const styleLabel =
        STYLE_LABELS[
            normalizeStyleForPack(
                parsed.style || 'solid',
                parsed.pack || 'classic'
            )
        ] || STYLE_LABELS.solid;
    const selectedPreviewClass = parsed.iconName
        ? buildIconValue(
              parsed.iconName,
              parsed.pack,
              normalizeStyleForPack(parsed.style, parsed.pack),
              parsed.extraTokens,
              '',
              null,
              false,
              null
          )
        : 'fa-solid fa-icons';
    const editorSelectedPreviewClass = stripSizeTokens(selectedPreviewClass);
    const selectedSummaryLabel = isCustomPack
        ? customValue
            ? 'Custom icon'
            : 'Choose custom icon'
        : selectedIconEntry?.label ||
          (parsed.iconName ? parsed.iconName : 'Choose icon');
    const selectedSummaryMeta = isCustomPack
        ? customValue
            ? 'Custom image or code'
            : 'Upload, select, or paste custom code'
        : parsed.iconName
        ? `${packLabel} · ${styleLabel}`
        : 'Click to browse icons';

    return (
        <div className="space-y-3">
            <div className="hover:bg-primary-100/70 flex w-full items-center rounded-xl border border-primary-200 bg-primary-50 shadow-sm transition-colors">
                <button
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-l-xl px-3 py-2 text-left focus:outline-none"
                    aria-expanded={isExpanded}
                >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary-200 bg-white text-primary-700 shadow-sm">
                        {isCustomPack && customValue ? (
                            /^<\s*/.test(customValue) ? (
                                <i
                                    className="fa-solid fa-code text-lg"
                                    aria-hidden="true"
                                />
                            ) : (
                                <img
                                    src={customValue}
                                    alt=""
                                    className="h-7 w-7 object-contain"
                                />
                            )
                        ) : (
                            <i
                                className={`${editorSelectedPreviewClass} text-lg`}
                                aria-hidden="true"
                            />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700">
                            Icon
                        </div>
                        <div className="truncate text-sm font-semibold text-neutral-900">
                            {selectedSummaryLabel}
                        </div>
                        <div className="truncate text-xs text-primary-700">
                            {selectedSummaryMeta}
                        </div>
                    </div>
                </button>
                {isCustomPack ? (
                    <button
                        type="button"
                        onClick={() => customUploadInputRef.current?.click()}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-primary-700 transition hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-300"
                        title="Upload custom icon"
                        aria-label="Upload custom icon"
                    >
                        <ArrowUpTrayIcon className="h-4 w-4" />
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    className="mr-2 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-primary-700 transition hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-300"
                    aria-label={
                        isExpanded ? 'Close icon picker' : 'Open icon picker'
                    }
                    aria-expanded={isExpanded}
                >
                    <ChevronDownIcon
                        className={`h-3 w-3 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                    />
                </button>
            </div>

            {isCustomPack ? (
                <div className="hidden" aria-hidden="true">
                    <ImageUpload
                        company_id={companyId}
                        file="custom-icon"
                        element="custom-icon-quick-upload"
                        src={customValue}
                        enableVideo={false}
                        showPreview={false}
                        showActionButtons={false}
                        uploadInputRef={customUploadInputRef}
                        onChange={({ value: nextValue }) =>
                            handleCustomValueChange(nextValue)
                        }
                    />
                </div>
            ) : null}

            {isExpanded ? (
                <div className="space-y-3 rounded-xl border border-primary-200 bg-white p-3 shadow-sm">
                    {!isCustomPack ? (
                        <div className="flex flex-wrap gap-2">
                            <div className="relative w-full rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                <i
                                    className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-3 text-[13px] text-neutral-500"
                                    aria-hidden="true"
                                />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={placeholder}
                                    className="h-9 w-full rounded-[10px] border-0 bg-white px-9 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                />
                                {search.trim().length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearch('');
                                            setShowAll(false);
                                            syncLockRef.current = true;
                                            if (selectedPack === 'brands') {
                                                setSelectedPack('classic');
                                                setSelectedStyle(
                                                    normalizeStyleForPack(
                                                        'solid',
                                                        'classic'
                                                    )
                                                );
                                            }
                                            setSelectedCategory('all');
                                        }}
                                        aria-label="Clear search"
                                        className="absolute inset-y-0 right-2 my-auto flex h-4 w-4 items-center justify-center rounded-full bg-neutral-200 text-neutral-600 hover:bg-neutral-300 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                    >
                                        <XMarkIcon className="h-3 w-3" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : null}

                    <div className="flex flex-wrap gap-2">
                        <Listbox
                            value={selectedPack}
                            onChange={handlePackChange}
                        >
                            <div className="relative inline-flex w-full rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600 sm:w-[calc(50%-0.25rem)]">
                                <Listbox.Button className="relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white pl-2 pr-6 text-left text-[11px] leading-5 text-neutral-800 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50">
                                    <span className="flex items-center gap-1.5 truncate">
                                        <i
                                            className="fa-solid fa-icons text-[12px] text-neutral-500"
                                            aria-hidden="true"
                                        />
                                        <span className="truncate font-semibold text-neutral-700">
                                            Pack
                                        </span>
                                        <span className="sr-only">
                                            Icon pack
                                        </span>
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                        <ChevronDownIcon className="h-3.5 w-3.5 text-neutral-500" />
                                    </span>
                                </Listbox.Button>
                                <Transition
                                    as={React.Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Listbox.Options className="absolute z-20 mt-1 w-full rounded-md border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                        {packOptions.map((pack) => (
                                            <Listbox.Option
                                                key={pack.key}
                                                value={pack.key}
                                                className={({ active }) =>
                                                    [
                                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                        active
                                                            ? 'bg-primary-50 text-primary-700'
                                                            : 'text-neutral-800',
                                                    ].join(' ')
                                                }
                                            >
                                                {({ selected }) => (
                                                    <>
                                                        <span>
                                                            {pack.label}
                                                        </span>
                                                        {selected && (
                                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </Transition>
                            </div>
                        </Listbox>

                        <Listbox
                            value={selectedStyle}
                            onChange={handleStyleChange}
                            disabled={isCustomPack || styleOptions.length <= 1}
                        >
                            <div
                                className={`relative inline-flex w-full rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600 sm:w-[calc(50%-0.25rem)] ${
                                    isCustomPack ? 'hidden' : ''
                                }`}
                            >
                                <Listbox.Button className="relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white pl-2 pr-6 text-left text-[11px] leading-5 text-neutral-800 focus:outline-none disabled:opacity-60">
                                    <span className="flex items-center gap-1.5 truncate">
                                        <i
                                            className="fa-solid fa-palette text-[12px] text-neutral-500"
                                            aria-hidden="true"
                                        />
                                        <span className="truncate font-semibold text-neutral-700">
                                            Style
                                        </span>
                                        <span className="sr-only">
                                            Icon style
                                        </span>
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                        <ChevronDownIcon className="h-3.5 w-3.5 text-neutral-500" />
                                    </span>
                                </Listbox.Button>
                                <Transition
                                    as={React.Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Listbox.Options className="absolute z-20 mt-1 w-full rounded-md border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                        {styleOptions.map((style) => (
                                            <Listbox.Option
                                                key={style.key}
                                                value={style.key}
                                                className={({ active }) =>
                                                    [
                                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                        active
                                                            ? 'bg-primary-50 text-primary-700'
                                                            : 'text-neutral-800',
                                                    ].join(' ')
                                                }
                                            >
                                                {({ selected }) => (
                                                    <>
                                                        <span>
                                                            {style.label}
                                                        </span>
                                                        {selected && (
                                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </Transition>
                            </div>
                        </Listbox>

                        <Listbox
                            value={selectedCategory}
                            onChange={setSelectedCategory}
                            disabled={isCustomPack || selectedPack === 'brands'}
                        >
                            <div
                                className={`relative inline-flex w-full rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600 sm:w-[calc(50%-0.25rem)] ${
                                    isCustomPack ? 'hidden' : ''
                                }`}
                            >
                                <Listbox.Button className="relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white pl-2 pr-6 text-left text-[11px] leading-5 text-neutral-800 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50">
                                    <span className="flex items-center gap-1.5 truncate">
                                        <i
                                            className="fa-solid fa-layer-group text-[12px] text-neutral-500"
                                            aria-hidden="true"
                                        />
                                        <span className="truncate font-semibold text-neutral-700">
                                            Category
                                        </span>
                                        <span className="sr-only">
                                            Category
                                        </span>
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                        <ChevronDownIcon className="h-3.5 w-3.5 text-neutral-500" />
                                    </span>
                                </Listbox.Button>
                                <Transition
                                    as={React.Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                        <Listbox.Option
                                            key="all"
                                            value="all"
                                            className={({ active }) =>
                                                [
                                                    'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                    active
                                                        ? 'bg-primary-50 text-primary-700'
                                                        : 'text-neutral-800',
                                                ].join(' ')
                                            }
                                        >
                                            {({ selected }) => (
                                                <>
                                                    <span>All categories</span>
                                                    {selected && (
                                                        <CheckIcon className="h-4 w-4 text-primary-600" />
                                                    )}
                                                </>
                                            )}
                                        </Listbox.Option>
                                        <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                                            Industries
                                        </div>
                                        {INDUSTRIES.map((industry) => (
                                            <Listbox.Option
                                                key={industry.key}
                                                value={industry.key}
                                                className={({ active }) =>
                                                    [
                                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                        active
                                                            ? 'bg-primary-50 text-primary-700'
                                                            : 'text-neutral-800',
                                                    ].join(' ')
                                                }
                                            >
                                                {({ selected }) => (
                                                    <>
                                                        <span>
                                                            {industry.label}
                                                        </span>
                                                        {selected && (
                                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                        <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                                            General
                                        </div>
                                        {GENERAL_GROUPS.map((group) => (
                                            <Listbox.Option
                                                key={group.key}
                                                value={group.key}
                                                className={({ active }) =>
                                                    [
                                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                        active
                                                            ? 'bg-primary-50 text-primary-700'
                                                            : 'text-neutral-800',
                                                    ].join(' ')
                                                }
                                            >
                                                {({ selected }) => (
                                                    <>
                                                        <span>
                                                            {group.label}
                                                        </span>
                                                        {selected && (
                                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </Transition>
                            </div>
                        </Listbox>

                        <Listbox
                            value={selectedAnimation}
                            onChange={handleAnimationChange}
                            disabled={isCustomPack}
                        >
                            <div
                                className={`relative inline-flex w-full rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600 sm:w-[calc(50%-0.25rem)] ${
                                    isCustomPack ? 'hidden' : ''
                                }`}
                            >
                                <Listbox.Button className="relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white pl-2 pr-6 text-left text-[11px] leading-5 text-neutral-800 focus:outline-none">
                                    <span className="flex items-center gap-1.5 truncate">
                                        <i
                                            className="fa-solid fa-wand-magic-sparkles text-[12px] text-neutral-500"
                                            aria-hidden="true"
                                        />
                                        <span className="truncate font-semibold text-neutral-700">
                                            Animation
                                        </span>
                                        <span className="sr-only">
                                            Icon animation
                                        </span>
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                        <ChevronDownIcon className="h-3.5 w-3.5 text-neutral-500" />
                                    </span>
                                </Listbox.Button>
                                <Transition
                                    as={React.Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Listbox.Options className="absolute z-20 mt-1 w-full rounded-md border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                        {ANIMATION_OPTIONS.map((anim) => (
                                            <Listbox.Option
                                                key={anim.id || 'none'}
                                                value={anim.id}
                                                className={({ active }) =>
                                                    [
                                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                        active
                                                            ? 'bg-primary-50 text-primary-700'
                                                            : 'text-neutral-800',
                                                    ].join(' ')
                                                }
                                            >
                                                {({ selected }) => (
                                                    <>
                                                        <span>
                                                            {anim.label}
                                                        </span>
                                                        {selected && (
                                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </Transition>
                            </div>
                        </Listbox>

                        {!isCustomPack && selectedAnimation && (
                            <div className="flex w-full flex-wrap items-center gap-3">
                                <div className="flex items-center gap-1.5 pr-1">
                                    <span className="text-xs font-semibold leading-5 text-neutral-800">
                                        Loop
                                    </span>
                                    <LoopToggle
                                        checked={loopEnabled}
                                        onChange={(checked) =>
                                            handleLoopToggle(checked)
                                        }
                                        ariaLabel="Toggle animation loop"
                                    />
                                </div>
                                <div className="flex items-center gap-2 pl-1">
                                    <span className="text-xs font-semibold leading-5 text-neutral-800">
                                        Duration
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="range"
                                            min={1}
                                            max={3}
                                            step={0.25}
                                            value={durationDisplayMs / 1000}
                                            onChange={(e) =>
                                                handleDurationChange(
                                                    parseFloat(e.target.value)
                                                )
                                            }
                                            disabled={!selectedAnimation}
                                            className="h-2 w-20 cursor-pointer appearance-none rounded-full bg-neutral-200 accent-primary-500 disabled:cursor-not-allowed"
                                        />
                                        <span className="w-10 text-right text-[11px] text-neutral-600">
                                            {(durationDisplayMs / 1000).toFixed(
                                                1
                                            )}
                                            s
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {isCustomPack ? (
                        <div className="rounded-lg border border-dashed border-neutral-200 p-2">
                            <MediaPanel
                                inline
                                hidePreview
                                enableVideo={false}
                                currentValue={customValue}
                                onSelectImage={handleCustomValueChange}
                                onSelectCustom={handleCustomValueChange}
                                onPreviewSelect={() => {}}
                            />
                        </div>
                    ) : null}
                    <div
                        className={`max-h-44 overflow-auto rounded-lg border border-dashed border-neutral-200 p-2 ${
                            isCustomPack ? 'hidden' : ''
                        }`}
                        onScroll={handleScroll}
                    >
                        {!shouldRenderGrid ? (
                            <div className="flex flex-col items-center justify-center gap-2 py-6 text-center text-xs text-neutral-500">
                                <div>
                                    Type to search or pick a category to load
                                    icons.
                                </div>
                                <button
                                    type="button"
                                    className="hover:bg-primary-50/50 rounded-md border border-neutral-200 px-3 py-1.5 text-[11px] text-neutral-700 hover:border-primary-200"
                                    onClick={() => setShowAll(true)}
                                >
                                    Show all icons
                                </button>
                            </div>
                        ) : limitedIcons.length === 0 && !showFallbackTile ? (
                            <div className="py-6 text-center text-xs text-neutral-500">
                                No icons match your filters.
                            </div>
                        ) : (
                            <>
                                <div className="mb-2 flex items-center justify-between text-[11px] text-neutral-500">
                                    <span>
                                        Showing {limitedIcons.length} of{' '}
                                        {filteredIcons.length} icons
                                    </span>
                                    {filteredIcons.length >
                                        limitedIcons.length && (
                                        <button
                                            type="button"
                                            className="rounded px-2 py-1 text-primary-700 hover:bg-primary-50"
                                            onClick={handleLoadMore}
                                        >
                                            Load more
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {limitedIcons.map((icon) => {
                                        const isSynthetic = !!icon.__synthetic;
                                        const iconIsBrand =
                                            icon?.variants?.length &&
                                            icon.variants.every(
                                                (v) => v.style === 'brands'
                                            );
                                        const previewPack = iconIsBrand
                                            ? 'brands'
                                            : selectedPack === 'brands'
                                            ? 'classic'
                                            : selectedPack;
                                        const previewStyle = iconIsBrand
                                            ? 'brands'
                                            : bestStyleForIcon(
                                                  icon,
                                                  previewPack,
                                                  selectedStyle
                                              );
                                        const selected =
                                            parsed.iconName === icon.name &&
                                            (isSynthetic ||
                                                parsed.pack === previewPack ||
                                                (iconIsBrand &&
                                                    parsed.pack === 'brands'));
                                        const previewClass = isSynthetic
                                            ? buildIconValue(
                                                  parsed.iconName,
                                                  parsed.pack,
                                                  normalizeStyleForPack(
                                                      parsed.style,
                                                      parsed.pack
                                                  ),
                                                  parsed.extraTokens,
                                                  '',
                                                  null,
                                                  false,
                                                  null
                                              )
                                            : buildIconValue(
                                                  icon.name,
                                                  previewPack,
                                                  previewStyle,
                                                  parsed.extraTokens,
                                                  '',
                                                  null,
                                                  false,
                                                  null
                                              );
                                        const editorPreviewClass =
                                            stripSizeTokens(previewClass);
                                        return (
                                            <button
                                                key={`${icon.name}-${selectedPack}-${selectedStyle}`}
                                                type="button"
                                                title={icon.label}
                                                aria-label={icon.label}
                                                onClick={() => {
                                                    handleIconSelect(icon);
                                                }}
                                                className={[
                                                    'flex h-8 w-8 items-center justify-center rounded-md border text-center text-neutral-700 transition',
                                                    selected
                                                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                                                        : 'hover:bg-primary-50/50 border-neutral-200 bg-white hover:border-primary-200',
                                                ].join(' ')}
                                            >
                                                <i
                                                    className={`${editorPreviewClass} text-lg`}
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        );
                                    })}
                                    {showFallbackTile && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const emit = buildIconValue(
                                                    guessedIconName,
                                                    selectedPack,
                                                    selectedStyle,
                                                    parsed.extraTokens,
                                                    selectedAnimation,
                                                    selectedAnimation &&
                                                        durationMs !== null
                                                        ? durationMs
                                                        : null,
                                                    loopEnabled,
                                                    selectedAnimation
                                                        ? animationRestartKey +
                                                              1
                                                        : null
                                                );
                                                const display = buildIconValue(
                                                    guessedIconName,
                                                    selectedPack,
                                                    selectedStyle,
                                                    parsed.extraTokens,
                                                    selectedAnimation,
                                                    selectedAnimation &&
                                                        durationMs !== null
                                                        ? durationMs
                                                        : null,
                                                    loopEnabled,
                                                    null
                                                );
                                                setManualValue(display);
                                                setAnimationRestartKey(
                                                    selectedAnimation
                                                        ? animationRestartKey +
                                                              1
                                                        : animationRestartKey
                                                );
                                                onChange?.(emit);
                                                if (selectedAnimation) {
                                                    dispatchRestart({
                                                        iconName:
                                                            guessedIconName,
                                                        className:
                                                            stripRestartTokens(
                                                                emit
                                                            ),
                                                    });
                                                }
                                            }}
                                            title={`Use “fa-${guessedIconName}”`}
                                            aria-label={`Use fa-${guessedIconName}`}
                                            className="hover:bg-primary-50/50 flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 bg-white text-center text-neutral-700 transition hover:border-primary-200"
                                        >
                                            <i
                                                className={`${stripSizeTokens(
                                                    buildIconValue(
                                                        guessedIconName,
                                                        selectedPack,
                                                        selectedStyle,
                                                        parsed.extraTokens,
                                                        '',
                                                        null,
                                                        false,
                                                        null
                                                    )
                                                )} text-lg`}
                                                aria-hidden="true"
                                            />
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
